import type { Transaction } from "@/entities/transaction/model/types";
import { dollarsToCents } from "@/shared/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  mapTransactionShortToUI,
  mapTransactionToUI,
  mapTransactionToUpdate,
} from "../transaction-mappers";
import {
  GetRefundsParams,
  GetTransactionsParams,
  TransactionConfigUpdateSchema,
  TransactionCreateSchema,
  TransactionOfferInputSchema,
  TransactionRefundCreateSchema,
  transactionsApi,
} from "../transactions";
import { applicationsKeys } from "./use-applications";

export const transactionsKeys = {
  all: ["transactions"] as const,
  lists: () => [...transactionsKeys.all, "list"] as const,
  list: (filters?: GetTransactionsParams) => [...transactionsKeys.lists(), filters] as const,
  details: () => [...transactionsKeys.all, "detail"] as const,
  detail: (id: string) => [...transactionsKeys.details(), id] as const,
  refunds: (transactionId: string) =>
    [...transactionsKeys.detail(transactionId), "refunds"] as const,
  refundsList: (transactionId: string, params?: GetRefundsParams) =>
    [...transactionsKeys.refunds(transactionId), params] as const,
};

export function useTransactions(params?: GetTransactionsParams) {
  return useQuery({
    queryKey: transactionsKeys.list(params),
    queryFn: async () => {
      const response = await transactionsApi.getTransactions(params);
      return {
        items: response.items.map(mapTransactionShortToUI),
        total: response.total,
        offset: response.offset,
        limit: response.limit,
      };
    },
  });
}

export function useTransaction(transactionId: string) {
  return useQuery({
    queryKey: transactionsKeys.detail(transactionId),
    queryFn: async () => {
      const data = await transactionsApi.getTransaction(transactionId);
      return mapTransactionToUI(data);
    },
    enabled: !!transactionId,
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TransactionCreateSchema) => {
      const apiData = await transactionsApi.createTransaction(data);
      return mapTransactionToUI(apiData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });

      if (variables.applicationId) {
        queryClient.invalidateQueries({
          queryKey: applicationsKeys.detail(variables.applicationId),
        });
        queryClient.refetchQueries({
          queryKey: applicationsKeys.detail(variables.applicationId),
        });
      }
    },
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      transaction,
    }: {
      transactionId: string;
      transaction: Partial<Transaction>;
    }) => {
      const updateData = mapTransactionToUpdate(transaction);
      const apiData = await transactionsApi.updateTransaction(transactionId, updateData);
      return mapTransactionToUI(apiData);
    },
    onMutate: async ({ transactionId, transaction }) => {
      await queryClient.cancelQueries({ queryKey: transactionsKeys.detail(transactionId) });

      const previousTransaction = queryClient.getQueryData<Transaction>(
        transactionsKeys.detail(transactionId)
      );

      if (previousTransaction) {
        queryClient.setQueryData<Transaction>(transactionsKeys.detail(transactionId), {
          ...previousTransaction,
          ...transaction,
        });
      }

      return { previousTransaction };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTransaction) {
        queryClient.setQueryData(
          transactionsKeys.detail(_variables.transactionId),
          context.previousTransaction
        );
      }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(transactionsKeys.detail(variables.transactionId), data);
      queryClient.invalidateQueries({
        queryKey: transactionsKeys.detail(variables.transactionId),
      });
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
    },
  });
}

export function useSignTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const apiData = await transactionsApi.signTransaction(transactionId);
      return mapTransactionToUI(apiData);
    },
    onSuccess: (data, transactionId) => {
      queryClient.setQueryData(transactionsKeys.detail(transactionId), data);
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
    },
  });
}

export function useRejectTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const apiData = await transactionsApi.rejectTransaction(transactionId);
      return mapTransactionToUI(apiData);
    },
    onSuccess: (data, transactionId) => {
      queryClient.setQueryData(transactionsKeys.detail(transactionId), data);
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
    },
  });
}

export function useCreateRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: TransactionRefundCreateSchema;
    }) => {
      return await transactionsApi.createRefund(transactionId, {
        ...data,
        amount: dollarsToCents(data.amount),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: transactionsKeys.detail(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: transactionsKeys.refunds(variables.transactionId),
      });
    },
  });
}

export function useRefunds(transactionId: string, params?: GetRefundsParams) {
  return useQuery({
    queryKey: transactionsKeys.refundsList(transactionId, params),
    queryFn: async () => {
      return await transactionsApi.getRefunds(transactionId, params);
    },
    enabled: !!transactionId,
  });
}

export function useUpdateTransactionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: TransactionConfigUpdateSchema;
    }) => {
      return await transactionsApi.updateTransactionConfig(transactionId, data);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(transactionsKeys.detail(variables.transactionId), data);
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
    },
  });
}

export function useAddTransactionOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: TransactionOfferInputSchema;
    }) => {
      return await transactionsApi.addOffer(transactionId, data);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(transactionsKeys.detail(variables.transactionId), data);
      queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
    },
  });
}
